import { useCallback } from 'react';
import {
	APPROVER_RECOMMENDATION,
	PROPOSER_RECOMMENDATION,
} from '../../shared/Routes';
import { Button, Grid, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useApplicationId } from '../../hooks/useApplicationId';
import { useLocation } from 'react-router-dom';
import {
	Answers,
	Feedback,
	UserRole,
} from '../../shared/VolunteerApplicationServiceTypes';
import { CategoryStepUIConfig } from '../../shared/CategoryStepUIConfig';
import { MultipleChoiceQuestionUIConfig } from '../../shared/MultipleChoiceQuestionUIConfig';
import { ApplicantAnswerToQuestion } from './ApplicantAnswerToQuestion';
import { useAuth } from '../../hooks/useAuth';

const { useBreakpoint } = Grid;
const { Text } = Typography;

export const ViewQuestionsAndAnswers = () => {
	const location = useLocation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { t } = useTranslation();
	const { navigateWithApplicationId } = useApplicationId();
	const { role } = useAuth();
	const { answers, uiConfig, result, name, reviewer } = location.state as {
		answers: Answers;
		uiConfig?: CategoryStepUIConfig;
		result: Feedback;
		name: string;
		reviewer: string;
	};

	const questions =
		{
			application: uiConfig?.questions,
			candidateInterview:
				uiConfig?.questionsToAskInApplicantInterview?.mandatoryQuestionsToAsk,
			preceptor:
				uiConfig?.questionsToAskInPreceptorInterview?.mandatoryQuestionsToAsk,
			family:
				uiConfig?.questionsToAskInFamilyMemberInterview
					?.mandatoryQuestionsToAsk,
		}[
			reviewer as 'application' | 'candidateInterview' | 'preceptor' | 'family'
		] || [];

	const allQuestions = questions.length ? (
		questions.map((question: MultipleChoiceQuestionUIConfig) => {
			const value = answers?.questionsAndAnswers.find(
				(answer) => answer.questionId === question.questionId
			);
			return (
				<div key={question.questionId} className="my-[8px]">
					<ApplicantAnswerToQuestion uiConfig={question} value={value} showBG />
				</div>
			);
		})
	) : (
		<div className="mt-5 p-4 min-h-24 font-sans font-normal text-sm text-gray-650 rounded-md border border-solid !border-gray-200">
			{result.comments}
		</div>
	);

	const handleBackClick = useCallback(() => {
		const navigateTo =
			role === UserRole.APPROVER
				? APPROVER_RECOMMENDATION
				: PROPOSER_RECOMMENDATION;
		navigateWithApplicationId(navigateTo);
	}, [navigateWithApplicationId, role]);

	return (
		<div className="flex justify-center bg-bground-blue">
			<div
				className={`flex flex-col h-full min-h-0 rounded px-5 py-4 ${
					isMobile ? 'w-full' : 'w-2/5 border border-solid !border-gray-200'
				}`}
			>
				<div className="mb-3 sm:mb-0 text-center">
					<Text className="h-10 font-sans font-bold text-lg text-gray-650 text-center">
						{t(uiConfig?.title || name)}
					</Text>
				</div>
				{allQuestions}
				<div className="relative mt-6 flex justify-center w-full before:absolute before:top-0 before:left-0 before:w-full before:h-2 before:bg-gradient-to-b before:from-gray-300 before:to-transparent">
					<Button
						className={`${isMobile ? 'w-full' : 'w-4/5'}`}
						type="primary"
						onClick={handleBackClick}
					>
						{t('back')}
					</Button>
				</div>
			</div>
		</div>
	);
};
